import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import LocationPicker from "@/components/LocationPicker";
import RainSurgeBanner from "@/components/RainSurgeBanner";
import { useCart } from "@/hooks/useCart";
import { CartItemsList } from "@/components/cart/CartItemsList";
import { CartConfirmDialog } from "@/components/cart/CartConfirmDialog";
import { getLastPosition, useLastPosition } from "@/components/GeoProvider";
import { MapPin, Truck, Calculator, Smartphone, Banknote, CreditCard, CheckCircle, ShieldCheck, Send } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Payment methods configuration
const PAYMENT_METHODS = {
  wave: {
    name: "Wave",
    icon: "wave",
    color: "#D4AF37",
    bgColor: "bg-[#D4AF37]/10",
    borderColor: "border-[#D4AF37]",
    description: "Paiement mobile rapide et sécurisé",
    requiresPhone: true
  },
  orange_money: {
    name: "Orange Money",
    icon: "orange_money",
    color: "#FF6600",
    bgColor: "bg-[#FF6600]/10",
    borderColor: "border-[#FF6600]",
    description: "Paiement via Orange Money",
    requiresPhone: true
  },
  free_money: {
    name: "Free Money",
    icon: "free_money",
    color: "#E30613",
    bgColor: "bg-[#E30613]/10",
    borderColor: "border-[#E30613]",
    description: "Paiement via Free Money",
    requiresPhone: true
  },
  cash: {
    name: "Espèces",
    icon: "cash",
    color: "#22C55E",
    bgColor: "bg-green-50",
    borderColor: "border-green-500",
    description: "Paiement à la livraison",
    requiresPhone: false
  }
};

// Payment method icon component
const PaymentIcon = ({ method, size = 24 }) => {
  const iconMap = {
    wave: (
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
        <circle cx="20" cy="20" r="20" fill="#D4AF37"/>
        <path d="M10 20C10 20 15 15 20 20C25 25 30 20 30 20" stroke="white" strokeWidth="3" strokeLinecap="round"/>
      </svg>
    ),
    orange_money: (
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
        <circle cx="20" cy="20" r="20" fill="#FF6600"/>
        <circle cx="20" cy="20" r="10" fill="white"/>
        <circle cx="20" cy="20" r="5" fill="#FF6600"/>
      </svg>
    ),
    free_money: (
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
        <circle cx="20" cy="20" r="20" fill="#E30613"/>
        <text x="20" y="26" textAnchor="middle" fill="white" fontSize="16" fontWeight="bold">F</text>
      </svg>
    ),
    cash: <Banknote size={size} className="text-green-600" />
  };
  return iconMap[method] || <CreditCard size={size} />;
};

export default function NewOrder({ user, onLogout }) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showPickupMap, setShowPickupMap] = useState(false);
  const [showDeliveryMap, setShowDeliveryMap] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState(null);
  const [sameRecipient, setSameRecipient] = useState(true);
  const [voucher, setVoucher] = useState(null); // {code, type}
  const [voucherCode, setVoucherCode] = useState("");
  const [voucherChecking, setVoucherChecking] = useState(false);
  const [orderData, setOrderData] = useState({
    order_type: "individual",
    customer_id: user.id,
    customer_email: user.email,
    customer_name: user.full_name,
    customer_phone: user.phone || "",
    recipient_name: "",
    recipient_phone: "",
    pickup_address: "",
    pickup_latitude: null,
    pickup_longitude: null,
    delivery_address: "",
    delivery_latitude: null,
    delivery_longitude: null,
    payment_method: "cash",
    payment_phone: "",
    items_amount: "",
    notes: ""
  });
  const cart = useCart();
  const fromCart = new URLSearchParams(window.location.search).get("cart") === "1" && cart.items.length > 0;
  const livePos = useLastPosition();
  const [recipientHint, setRecipientHint] = useState(null);
  const autoFilled = useRef(false);
  const clearAutoFill = () => {
    if (!autoFilled.current) return;
    autoFilled.current = false;
    setOrderData((d) => ({ ...d, delivery_latitude: null, delivery_longitude: null, delivery_address: "" }));
  };

  // Numéro du destinataire → sa position GPS s'il a accepté le partage ; sinon saisie manuelle
  const recipientDigits = (orderData.recipient_phone || "").replace(/\D/g, "");
  useEffect(() => {
    if (sameRecipient || recipientDigits.length < 9) {
      setRecipientHint(null);
      clearAutoFill();
      return;
    }
    let stale = false;
    const t = setTimeout(() => {
      axios.get(`${API}/users/recipient-location`, { params: { phone: recipientDigits } })
        .then(({ data }) => {
          if (stale) return;
          if (!data.found) {
            clearAutoFill();
            setRecipientHint({ found: false, text: "Destinataire non trouvé ou position non partagée : saisissez l'adresse de destination." });
            return;
          }
          autoFilled.current = true;
          setOrderData((d) => ({ ...d, delivery_latitude: data.latitude, delivery_longitude: data.longitude, delivery_address: data.address }));
          setRecipientHint({ found: true, text: `Position de ${data.first_name} ajoutée comme destination. Vous pouvez la modifier sur la carte.` });
        })
        .catch(() => !stale && setRecipientHint(null));
    }, 600);
    return () => { stale = true; clearTimeout(t); };
  }, [recipientDigits, sameRecipient]); // eslint-disable-line react-hooks/exhaustive-deps
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Pré-remplissage depuis la carte (accueil / « Commander d'ici »)
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const num = (k) => (q.get(k) ? parseFloat(q.get(k)) : null);
    const patch = {};
    if (num("pickup_lat") && num("pickup_lng")) {
      Object.assign(patch, { pickup_latitude: num("pickup_lat"), pickup_longitude: num("pickup_lng"), pickup_address: q.get("pickup_address") || "Ma position actuelle" });
    }
    if (num("delivery_lat") && num("delivery_lng")) {
      Object.assign(patch, { delivery_latitude: num("delivery_lat"), delivery_longitude: num("delivery_lng") });
    }
    if (q.get("delivery_address")) patch.delivery_address = q.get("delivery_address");
    // Position GPS captée à l'ouverture de l'app → adresse de départ par défaut
    const last = getLastPosition();
    if (!patch.pickup_latitude && last && q.get("cart") !== "1") {
      Object.assign(patch, { pickup_latitude: last.lat, pickup_longitude: last.lng, pickup_address: "Ma position actuelle" });
    }
    if (Object.keys(patch).length) setOrderData((d) => ({ ...d, ...patch }));
  }, []);

  // GPS arrivé après le montage (première ouverture / lien direct) → remplit le départ s'il est vide
  useEffect(() => {
    if (!livePos || fromCart) return;
    setOrderData((d) => (d.pickup_latitude || d.pickup_address ? d : { ...d, pickup_latitude: livePos.lat, pickup_longitude: livePos.lng, pickup_address: "Ma position actuelle" }));
  }, [livePos, fromCart]);
  const cartVendor = fromCart ? cart.items[0].vendor_id : null;
  const cartShop = fromCart ? cart.items[0].shop_id : null;

  // Panier boutique : montant articles = total panier
  useEffect(() => {
    if (fromCart) setOrderData((d) => ({ ...d, order_type: "boutique", items_amount: String(cart.total) }));
  }, [fromCart, cart.total]);

  // Panier boutique : départ pré-rempli avec l'adresse de la boutique
  useEffect(() => {
    if (!cartVendor) return;
    const url = cartShop ? `${API}/shops/${cartShop}` : `${API}/users/${cartVendor}`;
    axios.get(url).then(({ data }) => {
      const loc = data?.shop_location || (data?.latitude && data?.longitude ? { latitude: data.latitude, longitude: data.longitude } : null);
      if (loc) {
        const label = data.address || `Boutique : ${data.business_name || data.name || data.full_name || "partenaire"}`;
        setOrderData((d) => ({ ...d, pickup_address: label, pickup_latitude: loc.latitude, pickup_longitude: loc.longitude }));
      } else {
        const label = data?.address || data?.business_name || data?.name || data?.full_name;
        if (label) setOrderData((d) => ({ ...d, pickup_address: d.pickup_address || `Boutique : ${label}` }));
      }
    }).catch(() => {});
  }, [cartVendor, cartShop]);
  const [paymentInitiated, setPaymentInitiated] = useState(false);
  const [paymentInstructions, setPaymentInstructions] = useState(null);

  // Calculate delivery fee when both locations are set
  useEffect(() => {
    const calculateFee = async () => {
      if (orderData.pickup_latitude && orderData.pickup_longitude &&
          orderData.delivery_latitude && orderData.delivery_longitude) {
        try {
          const response = await axios.post(`${API}/delivery/calculate-fee`, {
            pickup_latitude: orderData.pickup_latitude,
            pickup_longitude: orderData.pickup_longitude,
            delivery_latitude: orderData.delivery_latitude,
            delivery_longitude: orderData.delivery_longitude
          });
          setDeliveryFee(response.data);
        } catch (error) {
          console.error("Error calculating fee:", error);
        }
      }
    };
    calculateFee();
  }, [orderData.pickup_latitude, orderData.pickup_longitude, orderData.delivery_latitude, orderData.delivery_longitude]);

  // === VOUCHER LOGIC ===
  const FREE_DELIVERY_CAP = 5000;
  const rawFee = deliveryFee?.delivery_fee || 0;
  const voucherDiscount = voucher?.type === "free_delivery" ? Math.min(rawFee, FREE_DELIVERY_CAP) : 0;
  const effectiveDeliveryFee = Math.max(0, rawFee - voucherDiscount);

  const validateVoucher = async () => {
    const code = voucherCode.trim().toUpperCase();
    if (!code) return;
    setVoucherChecking(true);
    try {
      const r = await axios.post(`${API}/loyalty/vouchers/validate`, {
        code, user_id: user.id,
      });
      setVoucher({ code, type: r.data.type });
      toast.success(r.data.message || "Voucher appliqué");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Code invalide");
      setVoucher(null);
    } finally {
      setVoucherChecking(false);
    }
  };

  const removeVoucher = () => {
    setVoucher(null);
    setVoucherCode("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!orderData.delivery_latitude || !orderData.delivery_longitude) {
      toast.error("Choisissez la destination sur la carte (bouton épingle) pour que le livreur la trouve");
      setShowDeliveryMap(true);
      return;
    }
    if (fromCart) {
      setConfirmOpen(true);
      return;
    }
    submitOrder();
  };

  const submitOrder = async () => {
    setIsLoading(true);

    try {
      const itemsAmount = parseFloat(orderData.items_amount) || 0;
      const totalAmount = itemsAmount + effectiveDeliveryFee;

      // Validate phone for mobile money payments
      const selectedMethod = PAYMENT_METHODS[orderData.payment_method];
      if (selectedMethod?.requiresPhone && !orderData.payment_phone) {
        toast.error("Veuillez entrer votre numéro de téléphone pour ce mode de paiement");
        setIsLoading(false);
        return;
      }

      const orderPayload = {
        ...orderData,
        items_amount: itemsAmount,
        total_amount: totalAmount,
        delivery_fee: effectiveDeliveryFee,
        distance_km: deliveryFee?.distance_km || 0,
        platform_commission: deliveryFee?.platform_commission || 0,
        courier_earning: deliveryFee?.courier_earning || 0,
        recipient_name: sameRecipient ? orderData.customer_name : orderData.recipient_name,
        recipient_phone: sameRecipient ? orderData.customer_phone : orderData.recipient_phone,
        items: fromCart ? cart.items.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity })) : [],
        ...(fromCart ? { partner_id: cartVendor } : {})
      };

      const response = await axios.post(`${API}/orders`, orderPayload);
      const order = response.data;
      if (fromCart) cart.clear();

      // Redeem voucher if any (best-effort)
      if (voucher?.code) {
        try {
          await axios.post(`${API}/loyalty/vouchers/redeem`, {
            code: voucher.code, user_id: user.id, order_id: order.id,
          });
        } catch (vErr) {
          console.warn("Voucher redeem failed:", vErr);
        }
      }

      // Initiate payment based on method
      if (orderData.payment_method !== "cash") {
        // Lancer l'initiation du paiement, mais rediriger IMMÉDIATEMENT vers /tracking
        // (les instructions de paiement seront affichées via une bannière sur la page tracking).
        try {
          const paymentResponse = await axios.post(`${API}/paiements/initiate/${orderData.payment_method}`, {
            commande_id: order.id,
            telephone: orderData.payment_phone,
            montant: totalAmount
          });
          try {
            sessionStorage.setItem(`payment_${order.id}`, JSON.stringify(paymentResponse.data));
          } catch (storageErr) {
            console.warn("sessionStorage indisponible:", storageErr);
          }
        } catch (paymentErr) {
          console.warn("Erreur lors de l'initiation du paiement:", paymentErr);
        }
        toast.success(`Commande #${order.id.substring(0, 8)} créée ! Finalisez le paiement ${PAYMENT_METHODS[orderData.payment_method].name} ↓`);
        navigate(`/tracking?id=${order.id}`);
      } else {
        // Cash payment - create payment record
        await axios.post(`${API}/paiements/initiate/cash`, {
          commande_id: order.id,
          montant: totalAmount
        });

        toast.success("Commande créée avec succès! Suivez votre livraison.");
        navigate(`/tracking?id=${order.id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création de la commande");
    } finally {
      setIsLoading(false);
      setConfirmOpen(false);
    }
  };

  // Handle payment confirmation (mock - user clicks "J'ai payé")
  const handlePaymentConfirmation = async () => {
    if (!paymentInstructions?.payment_id) return;

    setIsLoading(true);
    try {
      await axios.put(`${API}/paiements/${paymentInstructions.payment_id}/confirm`);
      toast.success("Paiement confirmé! Votre commande est en cours de traitement.");
      navigate(`/tracking?id=${paymentInstructions.commande_id || paymentInstructions.order_id || ''}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la confirmation du paiement");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] text-white">
      {/* Overlay plein écran pendant la création de commande — évite la page blanche */}
      {isLoading && (
        <div
          className="fixed inset-0 z-[9999] bg-[#0F172A]/95 backdrop-blur-sm flex flex-col items-center justify-center p-6"
          data-testid="order-processing-overlay"
        >
          <div className="relative">
            <div className="w-20 h-20 rounded-full border-4 border-[#D4AF37]/20 border-t-[#D4AF37] animate-spin" />
            <Truck className="w-8 h-8 text-[#D4AF37] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="mt-6 text-xl font-black uppercase tracking-tight text-white">
            Votre commande est en cours de traitement
          </p>
          <p className="mt-2 text-sm text-[#D4AF37] font-bold uppercase tracking-widest">
            Nous recherchons le meilleur livreur…
          </p>
          <p className="mt-6 text-[11px] text-slate-400 max-w-sm text-center">
            Ne quittez pas cette page. Vous allez être redirigé vers le suivi dès que la course est acceptée.
          </p>
        </div>
      )}
      {/* Premium Header — "EXPÉDITION" */}
      <header className="p-6 pt-8 bg-[#1E293B] rounded-b-[3rem] shadow-2xl border-b border-white/5 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-56 h-56 bg-[#D4AF37]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-3xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <button
              onClick={() => navigate('/home')}
              className="bg-[#0F172A] border border-white/10 rounded-2xl px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-[#D4AF37] hover:border-[#D4AF37]/40"
              data-testid="new-order-back-btn"
            >
              ← Retour
            </button>
            <div className="text-center">
              <h1 className="text-xl md:text-2xl font-black uppercase tracking-tighter italic text-white" style={{fontFamily: 'Outfit, sans-serif'}}>
                EXPÉDITION
              </h1>
              <p className="text-[#D4AF37] text-[10px] font-bold uppercase tracking-[0.25em]">Standard ARTEKNOS Logistics</p>
            </div>
            <div className="bg-[#D4AF37]/10 p-3 rounded-2xl border border-[#D4AF37]/20">
              <ShieldCheck size={20} className="text-[#D4AF37]" />
            </div>
          </div>
          {/* Mode Pluie Dakar — surge auto Open-Meteo ou toggle admin */}
          <div className="mt-2 mb-2">
            <RainSurgeBanner />
          </div>

          {/* Itinéraire preview — dots bleu + or */}
          <div className="space-y-3">
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-[#003594] shadow-[0_0_10px_#003594]"></div>
              <div className="w-full bg-[#0F172A] border border-white/5 rounded-2xl py-3.5 pl-12 pr-4 text-xs font-medium text-white truncate">
                {orderData.pickup_address || <span className="text-slate-500">Point de ramassage — à définir ci-dessous</span>}
              </div>
            </div>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-[#D4AF37] shadow-[0_0_10px_#D4AF37]"></div>
              <div className="w-full bg-[#0F172A] border border-white/5 rounded-2xl py-3.5 pl-12 pr-4 text-xs font-medium text-white truncate">
                {orderData.delivery_address || <span className="text-slate-500">Destination — à définir ci-dessous</span>}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Section Flotte premium — estimation rapide */}
      {deliveryFee && (
        <section className="px-6 mt-8 max-w-3xl mx-auto" data-testid="new-order-fleet-preview">
          <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-4">Distance estimée · {deliveryFee.distance_km?.toFixed(1) || 0} km</h3>
          <div className="grid grid-cols-3 gap-3">
            {[
              { key: "moto", label: "Moto", Icon: Truck, multiplier: 1 },
              { key: "tricycle", label: "Break", Icon: Truck, multiplier: 1.8 },
              { key: "camion", label: "Camion", Icon: Truck, multiplier: 4 },
            ].map((v) => {
              const basePrice = Math.round((deliveryFee.delivery_fee || 1500) * v.multiplier / 100) * 100;
              const isActive = v.key === "moto"; // default moto recommended for standard orders
              return (
                <div
                  key={v.key}
                  className={`p-4 rounded-[1.5rem] border-2 transition-all cursor-default flex flex-col items-center gap-2 ${
                    isActive
                      ? "border-[#D4AF37] bg-[#D4AF37]/10 shadow-lg shadow-[#D4AF37]/10"
                      : "border-white/5 bg-[#1E293B]"
                  }`}
                  data-testid={`fleet-${v.key}`}
                >
                  <v.Icon size={22} className={isActive ? "text-[#D4AF37]" : "text-slate-500"} />
                  <div className="text-center">
                    <p className="text-[10px] font-black uppercase tracking-tight text-white">{v.label}</p>
                    <p className={`text-xs font-black mt-1 ${isActive ? "text-[#D4AF37]" : "text-slate-400"}`}>
                      {basePrice.toLocaleString("fr-FR")} F
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <div className="px-4 md:px-8 lg:px-12 py-8">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight text-white mb-6" style={{fontFamily: 'Outfit, sans-serif'}} data-testid="new-order-title">Détails de la commande</h2>
          <Card className="bg-[#1E293B] border border-white/5 rounded-[3rem] shadow-2xl">
            <CardContent className="p-6 md:p-8">
              <form onSubmit={handleSubmit} className="space-y-7">
                {/* ============ SECTION 1 — VOS INFORMATIONS ============ */}
                <div className="space-y-4" data-testid="newOrder-informations">
                  <h3 className="text-[10px] md:text-xs font-black uppercase tracking-[0.3em] text-[#D4AF37]">
                    1 · Vos informations
                  </h3>

                  <div>
                    <Label htmlFor="order-type" className="text-[10px] font-black uppercase text-[#D4AF37] tracking-[0.25em]">Type de service</Label>
                    <select
                      id="order-type"
                      className="mt-2 h-12 w-full rounded-2xl bg-[#0F172A] border border-white/10 px-3 py-2 text-base font-black text-white focus:outline-none focus:border-[#D4AF37]"
                      value={orderData.order_type}
                      onChange={(e) => setOrderData({...orderData, order_type: e.target.value})}
                      data-testid="order-type-select"
                    >
                      <option value="individual">Particulier</option>
                      <option value="commerce">Commerce en ligne</option>
                      <option value="boutique">Boutique</option>
                      <option value="tailor">Artisan</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="customer-name" className="text-[10px] font-black uppercase text-[#D4AF37] tracking-[0.25em]">Votre nom</Label>
                      <Input
                        id="customer-name"
                        value={orderData.customer_name}
                        onChange={(e) => setOrderData({...orderData, customer_name: e.target.value})}
                        required
                        data-testid="customer-name-input"
                        className="mt-2 bg-[#0F172A] border-white/10 text-base font-black text-white focus-visible:border-[#D4AF37]"
                      />
                    </div>
                    <div>
                      <Label htmlFor="customer-phone" className="text-[10px] font-black uppercase text-[#D4AF37] tracking-[0.25em]">Votre téléphone</Label>
                      <Input
                        id="customer-phone"
                        type="tel"
                        value={orderData.customer_phone}
                        onChange={(e) => setOrderData({...orderData, customer_phone: e.target.value})}
                        required
                      data-testid="customer-phone-input"
                        className="mt-2 bg-[#0F172A] border-white/10 text-base font-black text-white focus-visible:border-[#D4AF37]"
                      />
                    </div>
                  </div>

                  {/* Recipient toggle + form */}
                  <div className="border border-white/10 rounded-2xl p-4 bg-[#0F172A]/60">
                    <div className="flex items-center gap-2 mb-4">
                      <input
                        type="checkbox"
                        id="same-recipient"
                        checked={sameRecipient}
                        onChange={(e) => setSameRecipient(e.target.checked)}
                        className="w-4 h-4 accent-[#D4AF37]"
                        data-testid="same-recipient-checkbox"
                      />
                      <Label htmlFor="same-recipient" className="cursor-pointer text-sm font-bold text-white">
                        Je suis le destinataire de la livraison
                      </Label>
                    </div>

                    {!sameRecipient && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="recipient-name" className="text-[10px] font-black uppercase text-[#D4AF37] tracking-[0.25em]">Nom du destinataire</Label>
                          <Input
                            id="recipient-name"
                            placeholder="Nom complet"
                            value={orderData.recipient_name}
                            onChange={(e) => setOrderData({...orderData, recipient_name: e.target.value})}
                            required={!sameRecipient}
                            data-testid="recipient-name-input"
                            className="mt-2 bg-[#0F172A] border-white/10 text-base font-black text-white placeholder:text-slate-500 focus-visible:border-[#D4AF37]"
                          />
                        </div>
                        <div>
                          <Label htmlFor="recipient-phone" className="text-[10px] font-black uppercase text-[#D4AF37] tracking-[0.25em]">Téléphone</Label>
                          <Input
                            id="recipient-phone"
                            type="tel"
                            placeholder="+221 77 123 45 67"
                            value={orderData.recipient_phone}
                            onChange={(e) => setOrderData({...orderData, recipient_phone: e.target.value})}
                            required={!sameRecipient}
                            data-testid="recipient-phone-input"
                            className="mt-2 bg-[#0F172A] border-white/10 text-base font-black text-white placeholder:text-slate-500 focus-visible:border-[#D4AF37]"
                          />
                          {recipientHint && (
                            <p className={`text-[11px] mt-1.5 ${recipientHint.found ? "text-emerald-400" : "text-slate-400"}`} data-testid="recipient-location-hint">
                              {recipientHint.text}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/* ============ FIN SECTION 1 ============ */}

                {/* ============ ITINÉRAIRE — Cargo Style High Contrast ============ */}
                <div className="space-y-4" data-testid="newOrder-itineraire">
                  <h3 className="text-[10px] md:text-xs font-black uppercase tracking-[0.3em] text-[#D4AF37]">
                    2 · Itinéraire
                  </h3>

                  {/* Départ */}
                  <div className="bg-[#0F172A] border-2 border-[#003594] rounded-2xl p-4 md:p-5 shadow-lg shadow-blue-900/20">
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 rounded-full bg-[#003594] flex items-center justify-center flex-shrink-0 shadow-[0_0_15px_#003594]/60">
                        <MapPin className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <Label htmlFor="pickup-address" className="text-[10px] font-black uppercase tracking-[0.25em] text-[#D4AF37] block mb-1.5">
                          Départ
                        </Label>
                        <div className="flex gap-2">
                          <Input
                            id="pickup-address"
                            placeholder="Sélectionner le lieu de ramassage"
                            value={orderData.pickup_address}
                            onChange={(e) => setOrderData({...orderData, pickup_address: e.target.value})}
                            required
                            data-testid="pickup-address-input"
                            className="bg-transparent border-0 border-b border-white/10 rounded-none text-base md:text-lg font-black text-white placeholder:text-slate-500 px-0 focus-visible:ring-0 focus-visible:border-[#D4AF37]"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowPickupMap(!showPickupMap)}
                            className="flex-shrink-0 bg-[#003594]/15 border-[#003594]/40 text-blue-300 hover:bg-[#003594]/30 rounded-xl"
                            data-testid="toggle-pickup-map-btn"
                          >
                            <MapPin className="w-4 h-4" />
                          </Button>
                        </div>
                        {showPickupMap && (
                          <div className="mt-3">
                            <LocationPicker
                              initialAddress={orderData.pickup_address}
                              initialPosition={orderData.pickup_latitude ? { lat: orderData.pickup_latitude, lng: orderData.pickup_longitude } : null}
                              onLocationSelect={(location) => {
                                setOrderData((orderData) => ({
                                  ...orderData,
                                  pickup_address: location.address,
                                  pickup_latitude: location.lat,
                                  pickup_longitude: location.lng
                                }));
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Destination */}
                  <div className="bg-[#0F172A] border-2 border-[#D4AF37] rounded-2xl p-4 md:p-5 shadow-lg shadow-yellow-900/20">
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 rounded-full bg-[#D4AF37] flex items-center justify-center flex-shrink-0 shadow-[0_0_15px_#D4AF37]/60">
                        <MapPin className="w-5 h-5 text-[#0F172A]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <Label htmlFor="delivery-address" className="text-[10px] font-black uppercase tracking-[0.25em] text-[#D4AF37] block mb-1.5">
                          Destination
                        </Label>
                        <div className="flex gap-2">
                          <Input
                            id="delivery-address"
                            placeholder="Sélectionner la destination"
                            value={orderData.delivery_address}
                            onChange={(e) => setOrderData({...orderData, delivery_address: e.target.value})}
                            required
                            data-testid="delivery-address-input"
                            className="bg-transparent border-0 border-b border-white/10 rounded-none text-base md:text-lg font-black text-white placeholder:text-slate-500 px-0 focus-visible:ring-0 focus-visible:border-[#D4AF37]"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowDeliveryMap(!showDeliveryMap)}
                            className="flex-shrink-0 bg-[#D4AF37]/15 border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/30 rounded-xl"
                            data-testid="toggle-delivery-map-btn"
                          >
                            <MapPin className="w-4 h-4" />
                          </Button>
                        </div>
                        {showDeliveryMap && (
                          <div className="mt-3">
                            <LocationPicker
                              initialAddress={orderData.delivery_address}
                              initialPosition={orderData.delivery_latitude ? { lat: orderData.delivery_latitude, lng: orderData.delivery_longitude } : null}
                              onLocationSelect={(location) => {
                                autoFilled.current = false;
                                setOrderData({
                                  ...orderData,
                                  delivery_address: location.address,
                                  delivery_latitude: location.lat,
                                  delivery_longitude: location.lng
                                });
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* ============ SECTION 3 — DÉTAILS & RÉCAP — High Contrast ============ */}
                <div className="space-y-4" data-testid="newOrder-details">
                  <h3 className="text-[10px] md:text-xs font-black uppercase tracking-[0.3em] text-[#D4AF37]">
                    3 · Détails & Récapitulatif
                  </h3>

                  {/* Delivery Fee Display */}
                  {deliveryFee && (
                    <div className="bg-[#0F172A] border-2 border-emerald-500 rounded-2xl p-4 md:p-5 shadow-lg shadow-emerald-900/20">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-9 h-9 rounded-xl bg-emerald-500 flex items-center justify-center">
                          <Truck className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-black text-white uppercase tracking-widest text-sm">Frais de livraison calculés</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-[10px] font-black uppercase text-[#D4AF37] tracking-[0.25em]">Distance</p>
                          <p className="text-base font-black text-white mt-0.5">{deliveryFee.distance_km} km</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-black uppercase text-[#D4AF37] tracking-[0.25em]">Frais</p>
                          <p className="text-base font-black text-emerald-400 mt-0.5">{deliveryFee.delivery_fee.toLocaleString("fr-FR")} FCFA</p>
                        </div>
                      </div>
                      <p className="text-xs text-slate-300 mt-3 font-medium">
                        Base : {deliveryFee.base_fee} FCFA + {deliveryFee.per_km_rate} FCFA/km
                      </p>
                    </div>
                  )}

                  {fromCart ? (
                    <div data-testid="neworder-cart-summary">
                      <Label className="text-[10px] font-black uppercase text-[#D4AF37] tracking-[0.25em]">Votre panier</Label>
                      <div className="mt-2">
                        <CartItemsList items={cart.items} onQty={cart.setQty} />
                      </div>
                    </div>
                  ) : (
                  <div>
                    <Label htmlFor="amount" className="text-[10px] font-black uppercase text-[#D4AF37] tracking-[0.25em]">Valeur des articles (FCFA) — optionnel</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="100"
                      placeholder="Ex : 5 000"
                      value={orderData.items_amount}
                      onChange={(e) => setOrderData({...orderData, items_amount: e.target.value})}
                      data-testid="amount-input"
                      className="mt-2 bg-[#0F172A] border-white/10 text-base font-black text-white placeholder:text-slate-500 focus-visible:border-[#D4AF37]"
                    />
                    <p className="text-xs text-slate-400 mt-1 font-medium">Laissez vide pour une simple livraison de colis</p>
                  </div>
                  )}

                {/* Total Summary — High Contrast */}
                {(deliveryFee || orderData.items_amount) && (
                  <div className="bg-[#0F172A] border-2 border-[#003594] rounded-2xl p-5 shadow-lg shadow-blue-900/20">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-9 h-9 rounded-xl bg-[#003594] flex items-center justify-center">
                        <Calculator className="w-4 h-4 text-white" />
                      </div>
                      <span className="font-black text-white uppercase tracking-widest text-sm">Récapitulatif</span>
                    </div>
                    <div className="space-y-2 text-base">
                      {orderData.items_amount && (
                        <div className="flex justify-between text-white font-medium">
                          <span>Valeur articles :</span>
                          <span className="font-black">{parseFloat(orderData.items_amount || 0).toLocaleString("fr-FR")} FCFA</span>
                        </div>
                      )}
                      <div className="flex justify-between text-white font-medium">
                        <span>Frais livraison :</span>
                        <span className={`font-black ${voucherDiscount > 0 ? "line-through text-slate-500" : ""}`}>
                          {rawFee.toLocaleString("fr-FR")} FCFA
                        </span>
                      </div>
                      {voucherDiscount > 0 && (
                        <div className="flex justify-between text-emerald-400 font-black" data-testid="voucher-discount-row">
                          <span>Voucher {voucher.code} :</span>
                          <span>−{voucherDiscount.toLocaleString("fr-FR")} FCFA</span>
                        </div>
                      )}
                      <div className="flex justify-between text-lg pt-3 border-t border-white/10">
                        <span className="text-[#D4AF37] font-black uppercase tracking-widest text-sm">Total</span>
                        <span className="text-[#D4AF37] font-black">
                          {(parseFloat(orderData.items_amount || 0) + effectiveDeliveryFee).toLocaleString("fr-FR")} FCFA
                        </span>
                      </div>
                    </div>

                    {/* Voucher input — High Contrast */}
                    <div className="mt-4 pt-4 border-t border-white/10" data-testid="voucher-section">
                      {voucher ? (
                        <div className="flex items-center justify-between bg-emerald-500/15 border border-emerald-500/40 rounded-xl p-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-black uppercase tracking-widest text-emerald-300">✓ {voucher.code}</span>
                            <span className="text-[10px] text-emerald-300 font-bold uppercase tracking-widest">Livraison offerte (≤ 5 000 F)</span>
                          </div>
                          <button
                            type="button"
                            onClick={removeVoucher}
                            className="text-emerald-300 text-xs font-black uppercase tracking-widest hover:text-white"
                            data-testid="voucher-remove-btn"
                          >
                            Retirer
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={voucherCode}
                            onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                            placeholder="Code promo (ex : FREE-XXXX)"
                            className="flex-1 px-4 py-3 rounded-xl border border-white/10 bg-[#1E293B] text-sm font-mono uppercase text-white placeholder:text-slate-500 focus:outline-none focus:border-[#D4AF37]"
                            data-testid="voucher-code-input"
                          />
                          <button
                            type="button"
                            onClick={validateVoucher}
                            disabled={voucherChecking || !voucherCode.trim()}
                            className="px-5 py-3 rounded-xl bg-[#003594] hover:bg-[#003380] text-white text-xs font-black uppercase tracking-widest disabled:opacity-40 shadow-lg shadow-blue-900/30"
                            data-testid="voucher-apply-btn"
                          >
                            {voucherChecking ? "..." : "Appliquer"}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                </div>
                {/* ============ FIN SECTION 3 ============ */}

                {/* ============ SECTION 4 — PAIEMENT ============ */}
                <div className="space-y-4">
                  <h3 className="text-[10px] md:text-xs font-black uppercase tracking-[0.3em] text-[#D4AF37]">
                    4 · Méthode de paiement
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(PAYMENT_METHODS).map(([key, method]) => (
                      <div
                        key={key}
                        onClick={() => setOrderData({...orderData, payment_method: key, payment_phone: key !== 'cash' ? orderData.customer_phone : ''})}
                        className={`relative p-4 rounded-2xl border-2 cursor-pointer transition-all bg-[#0F172A] ${
                          orderData.payment_method === key
                            ? 'border-[#D4AF37] shadow-lg shadow-yellow-900/30'
                            : 'border-white/10 hover:border-white/30'
                        }`}
                        data-testid={`payment-method-${key}`}
                      >
                        {orderData.payment_method === key && (
                          <CheckCircle className="absolute top-2 right-2 w-5 h-5 text-[#D4AF37]" />
                        )}
                        <div className="flex items-center gap-3">
                          <PaymentIcon method={key} size={32} />
                          <div>
                            <p className={`font-black text-base ${orderData.payment_method === key ? 'text-[#D4AF37]' : 'text-white'}`}>
                              {method.name}
                            </p>
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">{method.description}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Phone input for mobile money */}
                  {PAYMENT_METHODS[orderData.payment_method]?.requiresPhone && (
                    <div className="p-4 rounded-2xl bg-[#0F172A] border border-white/10">
                      <Label htmlFor="payment-phone" className="flex items-center gap-2 text-[10px] font-black uppercase text-[#D4AF37] tracking-[0.25em]">
                        <Smartphone className="w-4 h-4" />
                        Numéro {PAYMENT_METHODS[orderData.payment_method].name}
                      </Label>
                      <Input
                        id="payment-phone"
                        type="tel"
                        placeholder="+221 77 123 45 67"
                        value={orderData.payment_phone}
                        onChange={(e) => setOrderData({...orderData, payment_phone: e.target.value})}
                        className="mt-2 bg-[#1E293B] border-white/10 text-base font-black text-white placeholder:text-slate-500 focus-visible:border-[#D4AF37]"
                        required
                        data-testid="payment-phone-input"
                      />
                      <p className="text-xs text-slate-400 mt-2 font-medium">
                        Vous recevrez une demande de paiement sur ce numéro
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="notes" className="text-[10px] font-black uppercase text-[#D4AF37] tracking-[0.25em]">Notes (optionnel)</Label>
                  <Textarea
                    id="notes"
                    placeholder="Instructions spéciales pour la livraison..."
                    value={orderData.notes}
                    onChange={(e) => setOrderData({...orderData, notes: e.target.value})}
                    rows={3}
                    data-testid="notes-textarea"
                    className="mt-2 bg-[#0F172A] border-white/10 text-base font-medium text-white placeholder:text-slate-500 focus-visible:border-[#D4AF37]"
                  />
                </div>

                <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/20 p-4 rounded-2xl flex items-center gap-3">
                  <ShieldCheck className="text-[#D4AF37] w-5 h-5 flex-shrink-0" />
                  <span className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-widest">
                    Paiement sécurisé ARTEKNOS activé
                  </span>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0F172A] hover:opacity-90 rounded-[2rem] py-6 font-black text-xs uppercase tracking-widest shadow-xl shadow-[#D4AF37]/20 transition-all active:scale-95 flex items-center justify-center gap-3"
                  disabled={isLoading}
                  data-testid="submit-order-btn"
                >
                  <Send className="w-4 h-4" />
                  {isLoading ? "Création..." : orderData.payment_method === "cash" ? "Confirmer l'expédition" : `Payer via ${PAYMENT_METHODS[orderData.payment_method]?.name}`}
                </Button>
              </form>

              <CartConfirmDialog
                open={confirmOpen}
                onOpenChange={setConfirmOpen}
                items={cart.items}
                itemsTotal={cart.total}
                deliveryFee={effectiveDeliveryFee}
                deliveryAddress={orderData.delivery_address}
                paymentLabel={PAYMENT_METHODS[orderData.payment_method]?.name}
                onConfirm={submitOrder}
                loading={isLoading}
              />

              {/* Payment Instructions Modal */}
              {paymentInitiated && paymentInstructions && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <Card className="w-full max-w-md">
                    <CardHeader className="text-center">
                      <div className="mx-auto mb-4">
                        <PaymentIcon method={paymentInstructions.methode} size={64} />
                      </div>
                      <CardTitle>Instructions de paiement</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-primary">
                          {paymentInstructions.montant?.toLocaleString("fr-FR")} FCFA
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Transaction: {paymentInstructions.transaction_id}
                        </p>
                      </div>

                      <div className="bg-slate-50 p-4 rounded-lg">
                        <p className="text-sm">{paymentInstructions.instructions}</p>
                      </div>

                      {paymentInstructions.mock && (
                        <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
                          <p className="text-xs text-amber-800">
                            <strong>Mode démo:</strong> Cliquez sur &quot;J&apos;ai payé&quot; pour simuler la confirmation du paiement.
                          </p>
                        </div>
                      )}

                      <div className="flex gap-3">
                        <Button
                          variant="outline"
                          className="flex-1"
                          onClick={() => {
                            setPaymentInitiated(false);
                            navigate('/dashboard');
                          }}
                          data-testid="payment-later-btn"
                        >
                          Payer plus tard
                        </Button>
                        <Button
                          className="flex-1"
                          onClick={handlePaymentConfirmation}
                          disabled={isLoading}
                          data-testid="payment-confirm-btn"
                        >
                          {isLoading ? "Confirmation..." : "J'ai payé"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
